import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Alert, AppBar, Box, Button, ButtonBase, Stack, Toolbar, Typography, Grid } from '@mui/material';
// import { loadCaptchaEnginge } from 'react-simple-captcha';
// hooks
import { m } from 'framer-motion';
import { useNavigate, useParams, useLocation } from 'react-router';
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { HEADER } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import LoginSignupDialog from './visitor/LoginSignupDialog';
import ForgotPasswordDialog from './forgotpassword/ForgotPasswordDialog';
import ResetPasswordDialog from './forgotpassword/ResetPasswordDialog';
import { useDispatch, useSelector } from '../../../redux/store';
import { openLoginSignup } from '../../../redux/slices/authPages';
import { openTransactionsDialog } from '../../../redux/slices/transactionsDialog';

import Image from '../../../components/Image';
import NotificationPopover from './NotificationPopover';
import AccountPopover from './AccountPopover';
import useLocales from '../../../hooks/useLocales';
import { fCurrency } from '../../../utils/formatNumber';
import palette from '../../../theme/palette';
import useAuth from '../../../hooks/useAuth';
import LanguagePopover from './LanguagePopover';
import DownloadAppAlert from './DownloadAppAlert';
import { setActiveTab } from '../../../redux/slices/game';
import { getUserMemberBalance } from '../../../redux/slices/lookup';

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ theme, showDownloadApp, isDesktop }) => ({
  ...cssStyles(theme).bgBlur(),
  width: '-webkit-fill-available',
  left: 'unset',
  right: 'unset',
  boxShadow: 'unset',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  top: showDownloadApp && !isDesktop ? 36 : 0,
  backgroundColor: isDesktop ? '#fff' : 'transparent',
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
}));

const HeaderMenuLabel = styled(Typography)(({ theme, isSelected }) => ({
  color: isSelected ? theme.palette.primary.light : theme.palette.primary.lightText,
  fontSize: 14,
  textAlign: 'center',
  fontWeight: 500,
  cursor: 'pointer',
  width: 'fit-content',
  borderBottom: `2px solid ${isSelected ? theme.palette.primary.light : 'transparent'}`,
}));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
  onToggleCollapse: PropTypes.func,
};

export default function DashboardHeader({
  isOpenSidebar = false,
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false,
  // onCloseSidebar,
  onToggleCollapse,
  showDownloadApp,
  setShowDownloadApp,
}) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;

  const location = useLocation();

  const { isAuthenticated } = useAuth();

  const isDesktop = useResponsive('up', 'lg');

  const isSmall = useResponsive('down', 'md');

  const { translate } = useLocales();

  const dispatch = useDispatch();

  const { isOpenLoginSignupDialog } = useSelector((x) => x.authPages);

  const { provider, memberBalance } = useSelector((x) => x.lookup);

  const { activeTab } = useSelector((x) => x.game);

  const [openForgotPasswordDialog, setOpenForgotPasswordDialog] = useState(false);

  const [openResetPasswordDialog, setOpenResetPasswordDialog] = useState(false);

  const [headerMenuSize, setHeaderMenuSize] = useState(1);

  const navigate = useNavigate();

  const params = useParams();

  const theme = useTheme();

  useEffect(() => {
    // if (!isAuthenticated && localStorage.getItem(`referralCode`)) {
    //   setTimeout(() => {
    //     navigate('/auth/register');
    //     // dispatch(openLoginSignup({ open: true, isLogin: false }));
    //   }, 1000);
    // }

    // Check if got resetpassword token
    if (localStorage.getItem(`resetToken`)) {
      setOpenResetPasswordDialog(true);
    }
  }, [params]);

  useEffect(() => {
    if (provider) setHeaderMenuSize(Object.keys(provider).length + 1);
  }, [provider]);

  const HeaderMenuItem = ({ event, label, headerMenuSize, activeTab }) => {
    return (
      <Grid item sm={12 / headerMenuSize} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <HeaderMenuLabel onClick={event} isSelected={activeTab === label}>
          {translate(label)}
        </HeaderMenuLabel>
      </Grid>
    );
  };

  return (
    <>
      {/* {showDownloadApp && !isDesktop && (
        <DownloadAppAlert navigate={navigate} setShowDownloadApp={setShowDownloadApp} translate={translate} />
      )} */}
      <RootStyle
        isCollapse={isCollapse}
        isOffset={isOffset}
        verticalLayout={verticalLayout}
        showDownloadApp={showDownloadApp}
        isDesktop={isDesktop}
      >
        <Toolbar
          sx={{
            minHeight: '100% !important',
            pl: { lg: 1 },
            pr: { lg: 2 },
          }}
        >
          {/* {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />} */}

          {!isDesktop && (
            <Stack direction="row" alignItems={'center'} sx={{ mr: 0, height: '100%', px: 1, width: '100%' }}>
              <Box
                component="img"
                src={process.env.REACT_APP_LOGO}
                alt="App Logo"
                onClick={() => navigate('/')}
                sx={{
                  width: { xs: 100, md: 120 },
                  ml: -2,
                  cursor: 'pointer',
                }}
              />
              <Box sx={{ flex: 1 }} />
              <LanguagePopover isMobile={true} isDrawerOpen={true} />
            </Stack>
          )}

          {isDesktop && (
            <Stack direction="row" alignItems={'center'} sx={{ flexGrow: 1 }}>
              <Box
                component="img"
                src={process.env.REACT_APP_LOGO}
                alt="App Logo"
                onClick={() => navigate('/')}
                sx={{
                  width: { xs: 100, md: 120 },
                  // ml: 4,
                  cursor: 'pointer',
                }}
              />
              <Grid container sx={{ width: '60%' }}>
                <HeaderMenuItem
                  event={() => {
                    if (location.pathname === '/') {
                      dispatch(setActiveTab('home'));
                    } else {
                      dispatch(setActiveTab('home'));
                      navigate('/');
                    }
                  }}
                  label="home"
                  headerMenuSize={headerMenuSize}
                  activeTab={activeTab}
                />
                {/* Categories */}
                {provider &&
                  Object.keys(provider).map((key) => (
                    <HeaderMenuItem
                      event={() => {
                        dispatch(setActiveTab(key));
                        if (location.pathname !== '/') navigate('/');
                      }}
                      label={key}
                      headerMenuSize={headerMenuSize}
                      activeTab={activeTab}
                    />
                  ))}
              </Grid>
            </Stack>
          )}

          {/* <Box sx={{ flexGrow: 1 }} /> */}
          <Stack direction="row" alignItems="center" spacing={1}>
            {!isAuthenticated ? (
              <>
                {isDesktop && (
                  <>
                    <Button
                      size={isSmall ? 'small' : 'medium'}
                      variant="outlined"
                      sx={{
                        borderWidth: '2px !important',
                        borderColor: palette.header.login_btn.border,
                        color: palette.header.login_btn.color,
                        borderRadius: '12px',
                        height: '36px',
                        width: '160px',
                        ':hover': { background: 'transparent', borderColor: palette.header.login_btn.border },
                        boxShadow: 'none',
                      }}
                      onClick={() => navigate('/auth/login')}
                    >
                      {translate('login')}
                    </Button>
                    <Button
                      size={isSmall ? 'small' : 'medium'}
                      variant="contained"
                      sx={{
                        background: palette.header.register_btn.bg,
                        color: palette.header.register_btn.color,
                        borderRadius: '12px',
                        height: '36px',
                        width: '160px',
                        '&:hover': { background: palette.header.register_btn.bg },
                      }}
                      onClick={() => navigate('/auth/register')}
                    >
                      {translate('sign_up')}
                    </Button>
                  </>
                )}
              </>
            ) : (
              <Stack direction={'row'} spacing={1} sx={{ alignItems: 'center' }}>
                {isDesktop && (
                  <>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      spacing={1}
                      sx={{
                        height: '36px',
                        border: '2px solid',
                        borderColor: 'primary.main',
                        p: 1,
                        borderRadius: '12px',
                        cursor: 'pointer',
                      }}
                      onClick={() => getUserMemberBalance()}
                    >
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: theme.palette.header.balance_button.color }}>{`${
                        memberBalance?.currencyCode
                      } ${fCurrency(parseInt(memberBalance?.balance, 10))}`}</Typography>
                      <Iconify
                        icon={'flowbite:refresh-outline'}
                        sx={{ width: '20px', height: '20px', color: 'primary.main' }}
                      />
                    </Stack>
                    <Button
                      size={isSmall ? 'small' : 'medium'}
                      variant="contained"
                      sx={{
                        background: palette.header.deposit_btn.bg,
                        borderRadius: '12px',
                        height: '36px',
                        width: '160px',
                      }}
                      onClick={() => navigate('/deposit')}
                    >
                      <span>{translate('deposit')}</span>
                      <Iconify
                        icon={'fluent:add-12-filled'}
                        sx={{ width: '18px', height: '18px', color: '#000', ml: '4px' }}
                      />
                    </Button>

                    <AccountPopover />
                  </>
                )}
              </Stack>
            )}
          </Stack>

          {isOpenLoginSignupDialog.open && (
            <LoginSignupDialog setOpenForgotPasswordDialog={setOpenForgotPasswordDialog} />
          )}

          {openForgotPasswordDialog && (
            <ForgotPasswordDialog
              openForgotPasswordDialog={openForgotPasswordDialog}
              setOpenForgotPasswordDialog={setOpenForgotPasswordDialog}
            />
          )}

          {openResetPasswordDialog && (
            <ResetPasswordDialog
              openResetPasswordDialog={openResetPasswordDialog}
              setOpenResetPasswordDialog={setOpenResetPasswordDialog}
            />
          )}
        </Toolbar>
      </RootStyle>
    </>
  );
}
